/* =============================================================
   supabase.js — Supabase client initialisation
   -------------------------------------------------------------
   Creates and exports a single Supabase client instance that
   every file in the app uses to talk to the database, the
   authentication system, and file storage.

   How to use it in any file:
     import { supabase } from '../lib/supabase'

   The client automatically:
     - Manages the user session (login / logout / refresh)
     - Attaches the logged-in user's token to every request
     - Respects Row Level Security — users only see their data
   ============================================================= */

import { createClient } from '@supabase/supabase-js'

/* ─── ENVIRONMENT VARIABLES ─────────────────────────────────── */
// Injected at build time by Create React App from the .env file
// (local) or from Cloudflare environment variables (production).
// The anon key is a public key — security is enforced by RLS,
// not by keeping the key secret.
const SUPABASE_URL      = process.env.REACT_APP_SUPABASE_URL
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY

/* ─── CLIENT INSTANCE ───────────────────────────────────────── */
// A single shared client for the entire app.
// Supabase persists the session automatically in localStorage,
// so the user stays logged in across page refreshes.
// Guard against multiple instances during React hot-module replacement (HMR).
// Without this, each HMR cycle creates a new GoTrueClient that conflicts
// with the previous one, causing silent hangs on signInWithPassword.
// We store the single instance on the window object so it survives re-imports.
if (!window._supabaseClient) {
  window._supabaseClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
      // ── Web Lock bypass ───────────────────────────────────────
      // Supabase's GoTrueClient uses navigator.locks (Web Locks API)
      // to coordinate token refreshes across tabs. Chrome "freezes"
      // background tabs mid-execution, which can leave a lock acquired
      // but never released. When the tab becomes active again, every
      // subsequent auth operation queues behind the stuck lock forever
      // → infinite spinners, all data disappears, new tabs also freeze
      // (Web Locks are shared across tabs of the same origin).
      //
      // Fix: replace the lock with a simple pass-through. This is safe
      // because the app is used by one IO manager per university session
      // (single-tab) and we don't need cross-tab refresh coordination.
      lock: async (_name, _acquireTimeout, fn) => fn(),
    },
  })
}

export const supabase = window._supabaseClient
